// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import loadQuotes from "@insite/client-framework/Store/Data/Quotes/Handlers/LoadQuotes";
import { getQuotesDataView } from "@insite/client-framework/Store/Data/Quotes/QuotesSelector";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import CardContainer, { CardContainerStyles } from "@insite/content-library/Components/CardContainer";
import CardList, { CardListStyles } from "@insite/content-library/Components/CardList";
import CardListHeading from "@insite/content-library/Components/CardListHeading";
import QuoteSummaryCard, { QuoteSummaryCardStyles } from "@insite/content-library/Components/QuoteSummaryCard";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
// custom
import Notify from "@bg/App/Components/Notify";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import DataTable, { DataTableProps, SortOrderOptions } from "@insite/mobius/DataTable";
import DataTableBody, { DataTableBodyProps } from "@insite/mobius/DataTable/DataTableBody";
import DataTableHead, { DataTableHeadProps } from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow, { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const session = getSession(state);
    const isAuthenticated = session.isAuthenticated && !session.isGuest;
    return {
        settingsCollection: getSettingsCollection(state),
        session,
        quotesDataView: getQuotesDataView(state, recentQuotesParameter),
        billTo: getBillToState(state, session.billToId).value,
        myQuotesPageNavLink: getPageLinkByPageType(state, "RfqMyQuotesPage"),
        canViewQuotes: isAuthenticated && (session.userRoles || "").indexOf("Requisitioner") === -1,
    };
};

const mapDispatchToProps = {
    loadQuotes,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface RecentQuotesStyles {
    cardList?: CardListStyles;
    noQuotesGridItem?: GridItemProps;
    noQuotesText?: TypographyPresentationProps;
    cardContainer?: CardContainerStyles;
    quoteSummaryCard?: QuoteSummaryCardStyles;

    tableWrapper?: InjectableCss;

    dataTable?: DataTableProps;
    dataTableHead?: DataTableHeadProps;
    dataTableHeaders?: DataTableHeaderProps;
    dataTableBody?: DataTableBodyProps;
    dataTableRow?: DataTableRowProps;
    dataCell?: InjectableCss;
    contentWrapper?: InjectableCss;
}

export const recentQuotesStyles: RecentQuotesStyles = {
    noQuotesGridItem: { width: 12 },
    tableWrapper: {
        css: css`
            width: 100%;
            overflow: auto;
            margin-top: -10px;
        `,
    },

    cardList: {
        gridContainer: {
            css: css`
                a[data-test-selector="cardListHeadingViewAllLink"] {
                    margin-right: 10px;
                    margin-top: 5px;
                }
            `,
        },
    },
    contentWrapper: {
        css: css`
            margin-top: 20px;
        `,
    },
};

const recentQuotesParameter = {
    page: 1,
    pageSize: 5,
};

const styles = recentQuotesStyles;

class RecentQuotes extends React.Component<Props> {
    componentDidMount() {
        if (this.props.canViewQuotes && !this.props.quotesDataView.value) {
            this.props.loadQuotes(recentQuotesParameter);
        }
    }

    render() {
        const { settingsCollection, session, quotesDataView, myQuotesPageNavLink, canViewQuotes, billTo } = this.props;
        if (!canViewQuotes) {
            return null;
        }

        const countriesCantAccessQuotes = (settingsCollection.customSetting as any)?.countriesCantAccessQuotes;
        const notAllowedToView = countriesCantAccessQuotes
            ?.split(",")
            .some((c: string) => c.toLowerCase() === billTo?.country?.abbreviation.toLowerCase());

        if (notAllowedToView) {
            return null;
        }
        const myQuotesPageUrl = myQuotesPageNavLink ? myQuotesPageNavLink.url : undefined;

        return (
            <StyledWrapper
                data-test-selector="cardListRecentWishLists"
                css={css`
                    .title {
                        padding-left: 0 !important;
                    }
                `}
            >
                <CardListHeading heading={translate("Recent Quotes")} viewAllUrl={myQuotesPageUrl} />
                <StyledWrapper {...styles.contentWrapper}>
                    {quotesDataView && quotesDataView.value && quotesDataView.value.length === 0 && (
                        <Notify
                            messageType="info"
                            body={
                                <Typography {...styles.noQuotesText}>{siteMessage("Rfq_NoQuotesMessage")}</Typography>
                            }
                        />
                    )}
                    {quotesDataView && quotesDataView.value && quotesDataView.value.length !== 0 && (
                        <StyledWrapper {...styles.tableWrapper}>
                            <DataTable {...styles.dataTable}>
                                <DataTableHead {...styles.dataTableHead}>
                                    <DataTableHeader tight title={translate("Quote #")} {...styles.dataTableHeaders}>
                                        {translate("Quote #")}
                                    </DataTableHeader>
                                    <DataTableHeader tight title={translate("Type")} {...styles.dataTableHeaders}>
                                        {translate("Type")}
                                    </DataTableHeader>
                                    <DataTableHeader tight title={translate("Status")} {...styles.dataTableHeaders}>
                                        {translate("Status")}
                                    </DataTableHeader>
                                    <DataTableHeader tight title={translate("Requested")} {...styles.dataTableHeaders}>
                                        {translate("Requested")}
                                    </DataTableHeader>
                                    <DataTableHeader tight title={translate("Expires")} {...styles.dataTableHeaders}>
                                        {translate("Expires")}
                                    </DataTableHeader>
                                    {session.isSalesPerson && (
                                        <DataTableHeader
                                            tight
                                            title={translate("Sales Rep")}
                                            {...styles.dataTableHeaders}
                                        >
                                            {translate("Sales Rep")}
                                        </DataTableHeader>
                                    )}
                                    <DataTableHeader tight title={translate("Customer")} {...styles.dataTableHeaders}>
                                        {translate("Customer")}
                                    </DataTableHeader>
                                    <DataTableHeader
                                        tight
                                        title={translate("Ship To / Pick Up")}
                                        {...styles.dataTableHeaders}
                                    >
                                        {translate("Ship To / Pick Up")}
                                    </DataTableHeader>
                                </DataTableHead>
                                <DataTableBody {...styles.dataTableBody}>
                                    {(quotesDataView.value || []).map(quote => (
                                        <DataTableRow
                                            {...styles.dataTableRow}
                                            data-test-selector="recentQuotess_dataLine"
                                            key={quote.id}
                                        >
                                            <QuoteSummaryCard
                                                quote={quote}
                                                session={session}
                                                quoteSettings={settingsCollection.quoteSettings}
                                                extendedStyles={styles.quoteSummaryCard}
                                            />
                                        </DataTableRow>
                                    ))}
                                </DataTableBody>
                            </DataTable>
                        </StyledWrapper>
                    )}
                </StyledWrapper>
            </StyledWrapper>
        );
    }
}

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(RecentQuotes),
    definition: {
        group: "XNGAGE" as any,
        icon: "list-ul",
    },
};

export default widgetModule;
